import { useCallback, useMemo, useContext, useEffect, useState, ChangeEvent } from 'react'
import classNames from 'classnames/bind'
import isEmpty from 'ramda/es/isEmpty'
import map from 'ramda/es/map'
import dissoc from 'ramda/es/dissoc'
import { useTranslation } from 'react-i18next'
import moment from 'moment-timezone'

import config from 'app/config'
import RowLayout from 'app/common/layouts/RowLayout'
import Prompt from 'app/components/common/Prompt'
import { SellPointsContext } from 'app/common/components-next/sell-points-data'
import { useCurrencySign } from 'app/common/components-next/currency'
import PlainList from 'app/common/ui-next/plain-list'
import SimpleDate from 'app/common/classes/simple-date'

import OrderItem from '../../commonComponents/OrderItem'
import EmptyOrder from '../../commonComponents/EmptyOrder'
import useOnOpen from './useOnOpen'
import OrderFormHeader from '../../commonComponents/OrderFormHeader'
import OrderFormFooter from '../../commonComponents/OrderFormFooter'
import ExOrderSelectActions from './ExOrderSelectActions'
import { OrderDataContext, type OrderType } from '../OrderData'
import useSubmitOrder from './useSubmitOrder'
import ExOrderChanges from './ExOrderChanges'
import SuccessModal from './SuccessModal'

import styles from './OrderForm.module.scss';

const cx = classNames.bind(styles)

type Props = {
  date: SimpleDate
  open: boolean
  balance?: number
  balanceAvailable?: number
  personId: number
  personName: {
    first: string
    last: string
    middle: string
  }
  onClose: (e?: any) => void
  route?: unknown
  onOrderSuccess?: () => void
}

const OrderForm = ({
  date,
  open,
  balance = 0,
  balanceAvailable = 0,
  personId,
  personName,
  onClose,
  route,
  onOrderSuccess,
}: Props) => {
  const dateString = date.getStringFormatted(SimpleDate.defaultDateFormat) // TODO временно, до полной замены

  const { t } = useTranslation('cafeteria')
  const currency = useCurrencySign()

  // данные заказа
  const {
    order,
    savedOrder,
    setOrder,
    initOrder,
    orderTotal,
    orderTotalConsideringFreeItems,
    modified,
    someItemsRemoved,
    somePricesChanged,
    itemCountIncrement,
    itemCountDecrement,
    itemRemove,
  } = useContext(OrderDataContext)

  const orderReset = useCallback(() => {
    setOrder({})
  }, [setOrder])

  const balanceLeft = balanceAvailable - orderTotal
  const balanceLeftConsideringFreeItems = balanceAvailable - orderTotalConsideringFreeItems

  const { currentSellPoint } = useContext(SellPointsContext)
  const { id: sellPointId, building_id: buildingId } = currentSellPoint || {}
  const emptyOrder = useMemo(
    () => ({
      sale_point_id: sellPointId,
      building_id: buildingId,
    }),
    [sellPointId, buildingId],
  )

  // при закрытии окна очищаем заказ
  useEffect(() => {
    if (!open) {
      initOrder(emptyOrder)
    }
  }, [open, initOrder, emptyOrder])

  // функция закрытия с предупреждением
  const handleClose = useCallback(
    (e?: ChangeEvent<HTMLElement>) => {
      if (e && e.preventDefault) {
        e.preventDefault()
      }
      if (typeof onClose !== 'function') {
        return
      }
      const confirmMessage = t(
        'order_form_prompt_close',
        'Заказ не был отправлен. Данные заказа будут утеряны, если закрыть окно. Продолжить?',
      )
      // eslint-disable-next-line no-restricted-globals
      if (modified && !confirm(confirmMessage)) {
        return
      }
      onClose()
    },
    [onClose, modified, t],
  )

  // покупка используемая в заказе (ownOrder) взятая из апи покупок,
  // процесс загрузки их (loadingPurchases),
  // связанные запросы (request) и индикация загрузки этих запросов (occur)
  const [ownOrder, loadingPurchases, request, occur] = useOnOpen(
    open,
    handleClose,
    currentSellPoint,
    {
      date: dateString,
      customerId: personId,
    },
  )

  // требуется выбор действий, что делать с существующим заказом
  const [needChoice, setNeedChoice] = useState<boolean>(() =>
    Boolean(ownOrder && !isEmpty(ownOrder)),
  )
  useEffect(() => {
    setNeedChoice(Boolean(ownOrder && !isEmpty(ownOrder)))
  }, [ownOrder])

  // реинициализация заказа при поступлении новых покупок
  useEffect(() => {
    // @ts-ignore
    initOrder(!ownOrder || isEmpty(ownOrder) ? emptyOrder : (ownOrder as OrderType))
  }, [ownOrder, initOrder, emptyOrder])

  // hook отправки заказа на сервер
  // @ts-ignore
  const [handleOrderSubmit, sending, success] = useSubmitOrder(
    order,
    savedOrder,
    request as any,
    ownOrder,
    onClose as any,
  )

  // итемы заказа для отображения
  const tmpOccurDeleting = (occur as any).deleting
  const orderItems = useMemo(
    () =>
      map(
        ({ guid, cost, n, free_for_sale: free, ...other }) => ({
          key: guid,
          guid,
          // не доступно из-за нехватки баланса
          incDisabled: balanceLeftConsideringFreeItems - cost < 0, // с учетом бесплатных
          cost: free && n === 1 ? 0 : cost,
          balanceLeft,
          locked: loadingPurchases || tmpOccurDeleting || sending,
          n,
          ...other,
        }),
        order.items || [],
      ),
    [
      order,
      balanceLeft,
      sending,
      tmpOccurDeleting,
      loadingPurchases,
      balanceLeftConsideringFreeItems,
    ],
  )

  // заказ пустой
  const orderEmpty = isEmpty(orderItems)

  // удаление существующего (в покупках) заказа
  const deleteOrder = useCallback(
    () =>
      (request as any).post({
        path: 'cafeteria/customer/purchases',
        rest: { data: { ...order, status: -1 } },
        cbErrorTextCreate: () => t('order_form_error_fail_to_delete', 'Не удалось удалить заказ'),
        id: 'deleting',
        fnContentUpdate: (_, { purchases: p }) => ({ purchases: dissoc((order as any).guid, p) }),
      }),
    [order, request, t],
  )

  const header = useMemo(() => {
    const datePresentative = moment(dateString, config.dateFormat).format(
      config.dateFormatPresentative,
    )
    return [
      t('order_form_header_str1', { personFirstName: personName.first || '???' }),
      t('order_form_header_str2', { datePresentative }),
    ]
  }, [personName.first, dateString, t])

  const loading = loadingPurchases || tmpOccurDeleting

  return (
    <div className={cx(styles.root, { open })}>
      <OrderFormHeader
        className={styles.header}
        balance={balance}
        balanceAvailable={balanceLeftConsideringFreeItems < 0 ? 0 : balanceLeftConsideringFreeItems}
        onCrossClick={handleClose}
      >
        <div className={styles.title}>
          {header[0]}
          <br />
          {header[1]}
        </div>
      </OrderFormHeader>

      <RowLayout inside paddingfree borderTop className={styles.content}>
        {!orderEmpty ? (
          <PlainList id="order" autoHeight={false} scrollPadding={false}>
            {orderItems.map((props) => (
              // @ts-ignore
              <OrderItem
                {...props}
                key={props.guid}
                currency={currency}
                onCountIncrement={itemCountIncrement}
                onCountDecrement={itemCountDecrement}
                onRemove={itemRemove}
              />
            ))}
          </PlainList>
        ) : (
          <EmptyOrder loading={loading} />
        )}
      </RowLayout>

      <OrderFormFooter
        className={styles.footer}
        orderTotal={orderTotalConsideringFreeItems}
        loading={loadingPurchases}
        sending={sending}
        disabled={orderEmpty}
        onOrderClear={orderReset}
        onOrderSubmit={handleOrderSubmit}
      />

      {/* окно с выбором действий при открытии окна заказа и если найден существуюущий в покупках */}
      <ExOrderSelectActions
        onClose={handleClose}
        // @ts-ignore
        onDelete={deleteOrder}
        loading={loading}
        needChoice={needChoice}
        setNeedChoice={setNeedChoice}
      />

      {/* сообщение об изменении блюд в составе заказа */}
      <ExOrderChanges
        disabled={needChoice || !!success}
        someItemsRemoved={someItemsRemoved}
        somePricesChanged={somePricesChanged}
      />

      {/* сообщение об успешности отправки заказа */}
      <SuccessModal isOpen={!!success} preliminary={success === 2} onSuccess={onOrderSuccess} />

      <Prompt
        // @ts-ignore
        route={route}
        when={modified}
        message={
          t('order_form_prompt_unsaved', 'Вы не сохранили изменения, перейти в любом случае?') || ''
        }
      />
    </div>
  )
}

export default OrderForm
